import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { _isEmpty } from 'app/utils/helpers';
import STORAGE_CONSTANTS from 'app/constants/storage';
import Storage from 'app/utils/storage/local';
import { useRacesWidget } from 'app/hooks/useRacesWidget';
import API from 'app/utils/api/axios';
import userSlice from 'app/store/user/user.slice';
import API_URL from 'app/constants/api_urls';
import racesSlice from 'app/store/races/races.slice';
import { useGetWidgetRaces } from 'app/utils/api/queries/races.query';

export const useInitializeRaceSelection = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { data: widgetRaces } = useGetWidgetRaces({ display: 'menu' });
    const { currentRace } = racesSlice();
    const runOnceRef = useRef<boolean>(false);
    const { user, isAuthenticated } = userSlice((state) => state);
    const queryParams = new URLSearchParams(location.search);
    const raceFromParam = queryParams.get('race');
    const raceFromLocalStorage = Storage.get(`${STORAGE_CONSTANTS.currentRace}.${user?.email}`);
    const [searchParams, setSearchParams] = useSearchParams();
    const { handleSelectRaceString, stringifyRaceObject } = useRacesWidget();

    // Make sure there's a race selected at all times
    useEffect(() => {
        if (!isAuthenticated || !user.pk || !_isEmpty(currentRace)) return;

        const { pk: pkFromStorage } = JSON.parse(raceFromLocalStorage || '{}');
        const isRaceFromStorage = !_isEmpty(JSON.parse(raceFromLocalStorage || '{}'));
        const upcomingWidgetRaces = widgetRaces?.filter((race) => race.groupLabel === 'upcoming');
        const pastWidgetRaces = widgetRaces?.filter((race) => race.groupLabel === 'past');

        if (raceFromParam) {
            API.get(`${API_URL.RACE_DETAILS}${raceFromParam}/`)
                .then((response) => {
                    handleSelectRaceString(stringifyRaceObject(response.data));
                    searchParams.delete('race');
                    setSearchParams(searchParams);
                })
                .catch((error: AxiosError) => {
                    if (error.response?.status === 404) {
                        navigate('/404');
                    }
                });
            return;
        }

        // make sure pk exists in localstorage and is a valid pk
        if (
            !runOnceRef.current &&
            pkFromStorage &&
            typeof pkFromStorage === 'number' &&
            isRaceFromStorage
        ) {
            API.get(`${API_URL.RACE_DETAILS}${pkFromStorage}/`)
                .then((response) => {
                    if (response) {
                        handleSelectRaceString(stringifyRaceObject(response.data));
                    }
                })
                .catch(() => {
                    Storage.remove(`${STORAGE_CONSTANTS.currentRace}.${user?.email}`);
                });

            return;
        }

        if (!!upcomingWidgetRaces && upcomingWidgetRaces?.length > 0) {
            handleSelectRaceString(stringifyRaceObject(JSON.parse(upcomingWidgetRaces[0].value!)));
            return;
        }

        if (!!pastWidgetRaces && pastWidgetRaces?.length > 0) {
            handleSelectRaceString(
                stringifyRaceObject(JSON.parse(pastWidgetRaces[pastWidgetRaces.length - 1].value!))
            );
        }
    }, [
        handleSelectRaceString,
        isAuthenticated,
        navigate,
        raceFromParam,
        widgetRaces,
        searchParams,
        setSearchParams,
        stringifyRaceObject,
        user?.email,
        user.pk,
        raceFromLocalStorage,
        currentRace,
    ]);
};
